import React, { useEffect, useState } from "react";
import { QrCode, X, Loader2 } from "lucide-react";
import { http } from "@/lib/api";
import { resolveSiteSlug } from "@/config/siteConfig";

/**
 * IFT-branded "Install this app" card.
 *
 * - Listens for the beforeinstallprompt event and stashes it.
 * - Only surfaces on IFT hosts (indianfamilytree slug).
 * - Dismissal remembered for 30 days in localStorage.
 * - Auto-hides when already installed (display-mode standalone).
 * - Renders a "Scan to install on your phone" QR minted via Splink.pro
 *   so desktop visitors can hop the app onto their phone with one tap.
 * - Never blocks the page; sits above the footer at the bottom-right on
 *   desktop, and as a full-width slide-up card on mobile.
 */

const STORAGE_KEY = "iftInstallPromptDismissedAt";
const COOLDOWN_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
// Session-scoped cache so we mint the Splink QR at most once per tab.
const QR_CACHE = { url: null, promise: null };

function inStandalone() {
  if (typeof window === "undefined") return false;
  if (window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) return true;
  return window.navigator && window.navigator.standalone === true;
}

function recentlyDismissed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (!ts) return false;
    return Date.now() - ts < COOLDOWN_MS;
  } catch { return false; }
}

async function mintInstallQr() {
  if (QR_CACHE.url) return QR_CACHE.url;
  if (QR_CACHE.promise) return QR_CACHE.promise;
  QR_CACHE.promise = (async () => {
    const origin = typeof window !== "undefined" ? window.location.origin : "";
    const long_url = `${origin}/?utm_source=install-qr&utm_medium=web&utm_campaign=ift-pwa`;

    // 1) Preferred: mint via Splink.pro so the QR gets tracked analytics
    //    + a pretty splink.pro/xxx short link. Uses the tenant API key
    //    server-side (SPLINK_API_KEY_INDIANFAMILYTREE).
    try {
      const res = await http.post("/splink/shorten", {
        long_url,
        label: "IFT PWA install",
        site: resolveSiteSlug(),
      });
      const qr = res?.data?.qr_code_url || res?.data?.qr_url;
      const short = res?.data?.short_url;
      if (qr) {
        QR_CACHE.url = qr;
        QR_CACHE.shortUrl = short || long_url;
        return { qr, short: QR_CACHE.shortUrl };
      }
    } catch {
      // Splink unavailable — fall through to a client-side generator.
    }

    // 2) Fallback: render the QR from api.qrserver.com so the popup
    //    still works even if Splink is degraded. No analytics, but the
    //    install experience is preserved.
    const encoded = encodeURIComponent(long_url);
    const fallback = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=0&data=${encoded}`;
    QR_CACHE.url = fallback;
    QR_CACHE.shortUrl = long_url;
    return { qr: fallback, short: long_url };
  })();
  return QR_CACHE.promise;
}

export default function InstallPromptCard() {
  const [prompt, setPrompt] = useState(null);
  const [visible, setVisible] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [qrOpen, setQrOpen] = useState(false);
  const [qrUrl, setQrUrl] = useState(null);
  const [qrShort, setQrShort] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (resolveSiteSlug() !== "indianfamilytree") return;
    if (inStandalone()) return;
    if (recentlyDismissed()) return;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setPrompt(e);
      setTimeout(() => setVisible(true), 4000);
    };
    const onInstalled = () => {
      setVisible(false);
      try { localStorage.removeItem(STORAGE_KEY); } catch (_) {}
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  // Lazy-mint the QR the first time the user taps "Scan to install".
  useEffect(() => {
    if (!qrOpen || qrUrl || qrLoading) return;
    setQrLoading(true);
    mintInstallQr()
      .then((res) => {
        if (!res) return;
        setQrUrl(res.qr || null);
        setQrShort(res.short || null);
      })
      .finally(() => setQrLoading(false));
  }, [qrOpen, qrUrl, qrLoading]);

  if (!visible || !prompt) return null;

  const dismiss = () => {
    setVisible(false);
    try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
  };

  const install = async () => {
    setInstalling(true);
    try {
      prompt.prompt();
      const { outcome } = await prompt.userChoice;
      if (outcome !== "accepted") {
        try { localStorage.setItem(STORAGE_KEY, String(Date.now())); } catch (_) {}
      }
      setPrompt(null);
      setVisible(false);
    } finally {
      setInstalling(false);
    }
  };

  return (
    <div
      data-testid="ift-install-prompt"
      style={{
        position: "fixed",
        zIndex: 60,
        left: 16,
        right: 16,
        bottom: 16,
        maxWidth: 460,
        margin: "0 auto",
        background: "rgba(255,247,237,0.96)",
        border: "1px solid rgba(194,65,12,0.2)",
        borderRadius: 20,
        boxShadow: "0 24px 60px -20px rgba(194,65,12,0.4)",
        backdropFilter: "blur(14px)",
        WebkitBackdropFilter: "blur(14px)",
        padding: "16px 18px",
      }}
    >
      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <img
          src="/ift/icons/icon-192.png"
          alt="Indian Family Tree"
          width="52"
          height="52"
          style={{ borderRadius: 14, flex: "0 0 auto" }}
        />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 700, color: "#431a04", fontSize: 15, lineHeight: 1.2 }}>
            Keep your family close.
          </div>
          <div style={{ color: "#6b3410", fontSize: 12.5, lineHeight: 1.35, marginTop: 4 }}>
            Install Indian Family Tree for one-tap access to your tree, memorials and family stories.
          </div>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <button
            data-testid="ift-install-accept"
            onClick={install}
            disabled={installing}
            style={{
              appearance: "none", border: 0, background: "#C2410C", color: "#fff",
              padding: "8px 14px", borderRadius: 999, fontWeight: 600, fontSize: 13,
              cursor: "pointer", whiteSpace: "nowrap",
            }}
          >
            {installing ? "Installing…" : "Install"}
          </button>
          <button
            data-testid="ift-install-dismiss"
            onClick={dismiss}
            style={{
              appearance: "none", border: 0, background: "transparent", color: "#6b3410",
              padding: "4px 8px", fontSize: 12, cursor: "pointer",
            }}
          >
            Not now
          </button>
        </div>
      </div>

      {/* QR expand/collapse — desktop users can scan to install on phone */}
      <div style={{ marginTop: 10, borderTop: "1px solid rgba(194,65,12,0.14)", paddingTop: 10 }}>
        <button
          data-testid="ift-install-qr-toggle"
          onClick={() => setQrOpen((v) => !v)}
          aria-expanded={qrOpen}
          style={{
            appearance: "none", border: 0, background: "transparent",
            color: "#C2410C", fontSize: 12.5, fontWeight: 600,
            display: "inline-flex", alignItems: "center", gap: 6, cursor: "pointer",
            padding: 0,
          }}
        >
          <QrCode className="w-4 h-4" />
          {qrOpen ? "Hide QR" : "Scan to install on your phone"}
        </button>

        {qrOpen && (
          <div
            data-testid="ift-install-qr-panel"
            style={{
              marginTop: 10, display: "flex", gap: 12, alignItems: "center",
              background: "#fff", border: "1px solid rgba(194,65,12,0.15)",
              borderRadius: 14, padding: 10,
            }}
          >
            <div style={{
              width: 108, height: 108, flex: "0 0 auto",
              display: "grid", placeItems: "center",
              background: "#FFF7ED", borderRadius: 10,
              border: "1px dashed rgba(194,65,12,0.25)",
            }}>
              {qrLoading && <Loader2 className="w-5 h-5 animate-spin" style={{ color: "#C2410C" }} />}
              {!qrLoading && qrUrl && (
                <img
                  src={qrUrl}
                  alt="Scan to install Indian Family Tree on your phone"
                  width="96" height="96"
                  data-testid="ift-install-qr-image"
                  style={{ width: 96, height: 96, imageRendering: "pixelated" }}
                />
              )}
              {!qrLoading && !qrUrl && (
                <div style={{ fontSize: 10.5, color: "#6b3410", textAlign: "center", padding: 4 }}>
                  QR unavailable
                </div>
              )}
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12.5, color: "#431a04", fontWeight: 600, lineHeight: 1.25 }}>
                Point your phone camera at this square.
              </div>
              <div style={{ fontSize: 11.5, color: "#6b3410", lineHeight: 1.4, marginTop: 4 }}>
                Opens Indian Family Tree in your phone browser. Tap the browser menu and choose <b>Add to Home Screen</b> to install.
              </div>
              {qrShort && (
                <div
                  style={{ fontSize: 10.5, color: "#9a4a1c", marginTop: 6, fontFamily: "ui-monospace, SFMono-Regular, monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                  data-testid="ift-install-qr-short"
                  title={qrShort}
                >
                  {qrShort}
                </div>
              )}
            </div>
            <button
              aria-label="Close QR"
              onClick={() => setQrOpen(false)}
              style={{
                appearance: "none", border: 0, background: "transparent", color: "#6b3410",
                cursor: "pointer", padding: 4,
              }}
              data-testid="ift-install-qr-close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
